import { CSSProperties } from 'react'

import { IconX } from '@tabler/icons-react'
import styled, { css } from 'styled-components'

import { HEIGHTS } from './Button'

import { useLocale } from '@/context/localeContext'
import { colourShift, deterministicItemFromArray } from '@/utils/index'

import { buildPassTheme } from 'src/styles/theme'

type Size = 'small' | 'medium' | 'large'

type WrapperProps = {
  $size?: Size
  $capitalise?: boolean
  $clickable?: boolean
  $strong?: boolean
  $hasIcon: boolean
  $wrap?: boolean
}

const Wrapper = styled.div<WrapperProps>`
  ${({ theme }) => theme.font.caption01}
  font-weight: ${({ $strong }) => ($strong ? 700 : 400)};
  color: ${({ theme }) => theme.colour.secondary._600};
  height: ${({ $size, $wrap }) => ($wrap ? 'auto' : HEIGHTS[$size || 'small'])};
  padding-left: 0.5rem;
  padding-right: ${({ $hasIcon }) => ($hasIcon ? '0.625rem' : '0.5rem')};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: ${({ $capitalise }) =>
    $capitalise ? 'uppercase' : 'initial'};
  white-space: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};
  padding-top: ${({ $wrap }) => ($wrap ? '0.5rem' : '0')};
  padding-bottom: ${({ $wrap }) => ($wrap ? '0.5rem' : '0')};
  text-align: ${({ $wrap }) => ($wrap ? 'center' : 'left')};

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
    `}
`

type IconProps = {
  iconColour?: string
  haloColour?: string
}

const IconWrapper = styled.span<IconProps>`
  position: relative;
  display: inline-flex;

  ${({ iconColour }) =>
    iconColour &&
    css`
      svg {
        position: relative;
        color: ${iconColour};
      }
    `}

  ${({ haloColour }) =>
    haloColour &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: -0.125rem;
        left: -0.125rem;
        width: calc(100% + 0.25rem);
        height: calc(100% + 0.25rem);
        border-radius: 50%;
        background-color: ${haloColour};
      }
    `}
`

const CloseWrapper = styled.span`
  cursor: pointer;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  color: ${({ theme }) => theme.colour.secondary._300};
  background-color: ${({ theme }) => theme.colour.neutral.surface};
  display: flex;
  align-items: center;
  justify-content: center;
`

type OtherColours = keyof (typeof buildPassTheme)['colour']['other']

type Colours =
  | 'info'
  | 'warning'
  | 'success'
  | 'danger'
  | 'grey'
  | 'outline'
  | 'naked'
  | 'white'
  | OtherColours

export type TagVariant = Colours | { deterministicText: string }

const TAG_VARIANTS: Colours[] = [
  'success',
  'info',
  'warning',
  'danger',
  'grey',
  'hazel',
  'brown',
  'orange',
  'yellow',
  'lime',
  'pine',
  'cyan',
  'purple',
  'blue',
  'pink',
  'red',
  'darkRed',
  'white',
]

const getColours = (variant: TagVariant) => {
  let colour = null
  let bg = null
  let border = null

  if (typeof variant === 'object') {
    return getColours(
      deterministicItemFromArray(TAG_VARIANTS, variant.deterministicText)
    )
  }

  switch (variant) {
    case 'success':
      colour = buildPassTheme.colour.primary._900
      bg = buildPassTheme.colour.primary._50
      break
    case 'info':
      colour = buildPassTheme.colour.semantic.success
      bg = buildPassTheme.colour.semantic.successLight
      break
    case 'warning':
      colour = buildPassTheme.colour.semantic.warningDark
      bg = buildPassTheme.colour.semantic.warningLight
      break
    case 'danger':
      colour = buildPassTheme.colour.semantic.error
      bg = buildPassTheme.colour.semantic.errorLight
      break
    case 'grey':
    case undefined:
      colour = buildPassTheme.colour.secondary._600
      bg = buildPassTheme.colour.secondary._200
      break
    case 'blue':
      colour = buildPassTheme.colour.other.blue
      bg = '#EFF8FF'
      break
    case 'outline':
      colour = buildPassTheme.colour.secondary.base
      bg = 'transparent'
      border = `1px solid ${buildPassTheme.colour.secondary._100}`
      break
    case 'naked':
      colour = buildPassTheme.colour.secondary._500
      bg = 'transparent'
      break
    case 'white':
      colour = buildPassTheme.colour.secondary.base
      bg = buildPassTheme.colour.neutral.white
      break
    case 'purple':
      colour = buildPassTheme.colour.other.purple
      bg = '#7B68EE1A'
      break
    default:
      colour = variant
        ? buildPassTheme.colour.other[variant]
        : buildPassTheme.colour.secondary._500
      bg = colourShift(colour, 130)
  }

  return { colour, bg, border }
}

export type Props = {
  label: React.ReactNode
  icon?: React.ReactNode
  size?: Size
  className?: string
  variant?: TagVariant | { deterministicText: string }
  style?: CSSProperties
  capitalise?: boolean
  strong?: boolean
  closeable?: () => void
  colours?: { colour: string; bg: string; border?: string }
  onClick?: () => void
  wrap?: boolean
}

export type TagProps = Props

export const Tag: React.FC<Props> = ({
  label,
  icon,
  size = 'small',
  variant,
  className,
  colours,
  capitalise = true,
  strong = true,
  closeable,
  onClick,
  style,
  wrap = false,
  ...rest // for dropdown and popover reasons
}) => {
  const { colour, bg, border } = colours ?? getColours(variant)
  const { t } = useLocale()

  return (
    <Wrapper
      className={className}
      $size={size}
      $capitalise={capitalise}
      $strong={strong}
      $hasIcon={!!icon}
      $clickable={!!onClick}
      $wrap={wrap}
      onClick={onClick}
      style={{ color: colour, backgroundColor: bg, border, ...style }}
      {...rest}
    >
      {icon && <IconWrapper>{icon}</IconWrapper>}
      {typeof label === 'string' ? t(label) : label}
      {closeable && (
        <CloseWrapper onClick={closeable}>
          <IconX size={14} />
        </CloseWrapper>
      )}
    </Wrapper>
  )
}

Tag.displayName = 'Tag'
